<template>
  <v-container>
    <section class="ma-3 mb-0">
      <Transportadora />
    </section>
    <v-card class="ma-3 pa-6 elevation-0">
      <v-card-title class="app-card-title px-0">Taxas de Regulação</v-card-title>
      <v-card-subtitle class="px-0"
        >Apenas serão exibidas taxas a partir de 05/2026.
      </v-card-subtitle>
      <v-divider></v-divider>

      <v-alert
        v-if="!table.loading && table.items.length === 0"
        class="mt-4"
        type="warning"
        text
        border="left"
        prominent
      >
        <strong>Nenhuma taxa de regulação encontrada.</strong><br />
        Não foram encontradas taxas de regulação de transporte vinculadas ao seu CNPJ/CPF.
      </v-alert>

      <v-data-table
        v-else
        style="border: 0.01px #bebdbd solid"
        :headers="headers"
        :items="table.items"
        :loading="table.loading"
        :options.sync="pagination"
        :server-items-length="table.total"
        :footer-props="{
          showFirstLastPage: true,
          showCurrentPage: true,
          itemsPerPageOptions: [10, 20, 50],
        }"
        class="mt-4"
      >
        <template v-slot:item.competencia="{ item }">
          {{ formatDateMesAno(item.competencia) }}
        </template>
        <template v-slot:item.dataVencimento="{ item }">
          {{ formatDate(item.dataVencimento) }}
        </template>
        <template v-slot:item.valorTotal="{ item }"> R$ {{ item.valorTotal | monetary }} </template>
        <template v-slot:item.receitaStatus="{ item }">
          <v-chip dark :color="colorChips[item.receitaStatus] || 'grey lighten-1'" small>
            {{
              personalizarStatusParcela(item.receitaStatus)
                ? personalizarStatusParcela(item.receitaStatus).text
                : item.receitaStatus
            }}
          </v-chip>
        </template>
        <template v-slot:item.acoes="{ item }">
          <div class="d-flex align-center justify-center">
            <div style="width: 32px" class="d-flex justify-center">
              <v-tooltip top v-if="item.informacaoComplementar">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    style="cursor: pointer"
                    color="primary"
                    @click="abrirInformacaoComplementar(item)"
                  >
                    info
                  </v-icon>
                </template>
                <span>Ver informação complementar</span>
              </v-tooltip>
            </div>
            <div style="width: 32px" class="d-flex justify-center">
              <DialogBaixarDae
                v-if="!['PAGO', 'CANCELADO', 'PARCELADO'].includes(item.receitaStatus)"
                :idReceita="item.id"
                :cpfCnpj="transportadora.cnpj"
                :dataVencimento="obterUltimoDaeGerado(item).dataVencimento"
                @dae-gerado="loadTaxas"
              />
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogInfo.open" max-width="500">
      <v-card>
        <v-card-title class="app-card-title"> Informação complementar </v-card-title>
        <v-divider />
        <v-card-text
          class="pa-4"
          style="white-space: pre-line; max-height: 400px; overflow-y: auto"
        >
          {{ dialogInfo.texto }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="dialogInfo.open = false"> Fechar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { mapState } from 'vuex';
import Transportadora from '@/components/layout/Transportadora.vue';
import DialogBaixarDae from './components/DialogBaixarDae.vue';
import taxaRegulacaoService from '@/services/financeiro/taxa-regulacao.service';
import { RevenueStatusList } from '@/models/financeiro/enum/revenue-status.enum';
import { monetaryFormat } from '@/util/filters';

export default Vue.extend({
  name: 'TaxasRegulacao',
  components: {
    Transportadora,
    DialogBaixarDae,
  },
  filters: {
    monetary: monetaryFormat,
  },
  data() {
    return {
      table: {
        items: [],
        loading: false,
        total: 0,
      },
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['competencia'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      headers: [
        { text: 'Competência', value: 'competencia', sortable: true },
        { text: 'Data Vencimento', value: 'dataVencimento', sortable: true },
        { text: 'Valor', value: 'valorTotal', sortable: true },
        { text: 'Status', value: 'receitaStatus', sortable: true },
        { text: 'Ações', value: 'acoes', align: 'center', sortable: false },
      ],
      dialogInfo: {
        open: false,
        texto: '',
      },
      colorChips: {
        EM_ABERTO: 'primary lighten-1',
        PAGO: 'green lighten-1',
        PARCELADO: 'grey lighten-1',
        APURACAO: 'grey lighten-1',
        JURIDICO: 'grey lighten-1',
        DIVIDA_ATIVA: 'grey lighten-1',
        CANCELADO: 'red lighten-1',
        SUSPENSO: 'red lighten-1',
      },
    };
  },
  computed: {
    ...mapState('transportadora', ['transportadora']),
  },
  watch: {
    pagination: {
      handler(val, oldVal) {
        if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
          this.loadTaxas();
        }
      },
      deep: true,
    },
  },
  methods: {
    loadTaxas() {
      this.table.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      taxaRegulacaoService
        .listReceitas({
          cpfCnpj: this.transportadora.cnpj,
          page: page - 1,
          size: itemsPerPage,
          ascendingSort: sortDesc.length ? !sortDesc[0] : true,
          sort: sortBy,
        })
        .subscribe(
          res => {
            this.table.items = res?.content || [];
            this.table.total = res?.pageInfo?.totalElements || 0;
            this.table.loading = false;
          },
          () => {
            this.table.items = [];
            this.table.total = 0;
            this.table.loading = false;
          }
        );
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '';
    },
    formatDateMesAno(date) {
      return date ? moment(date).format('MM/YYYY') : '';
    },
    personalizarStatusParcela(status) {
      return RevenueStatusList.find(s => s.value == status);
    },
    obterUltimoDaeGerado(item) {
      if (!item || !Array.isArray(item.daes) || !item.daes.length) return item;

      return item.daes.reduce((ultimo, atual) =>
        moment(atual.dataCadastro).isAfter(moment(ultimo.dataCadastro)) ? atual : ultimo
      );
    },
    abrirInformacaoComplementar(item) {
      this.dialogInfo.texto = (item && item.informacaoComplementar) || '';
      this.dialogInfo.open = true;
    },
  },
  mounted() {
    this.loadTaxas();
  },
});
</script>
